import { Box, HStack, Icon, Link, Text } from "components/ui";
import { Typography } from "components/ui/Typography";
import { usePathname } from "expo-router";
import { Home } from "lucide-react-native";
import React from "react";
import { useWindowDimensions } from "react-native";
import { Category } from "store/abcoApi";

type Props = {
	idReplacement?: string | null | undefined;
	hierarchy?: Category[];
};

function capitalizeWordsAndRemoveDashes(str: string) {
	if( !str) return "";

	return str
		.split("-")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");
}

function formatSegment(segment: string, idReplacement?: string | null | undefined) {
	const isNumeric = /^\d+$/.test(segment);
	const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);

	if ((isNumeric || isGuid) && idReplacement) {
		return idReplacement;
	}

	return capitalizeWordsAndRemoveDashes(segment);
}

function flattenHierarchy(nodes: Category[]): { name: string; url: string }[] {
	const result: { name: string; url: string }[] = [];

	let current = nodes[0];
	while (current) {
		result.push({ name: current.name, url: current.url });
		current = current.children?.[0] ?? null;
	}

	return result;
}

const Breadcrumbs = ({ idReplacement, hierarchy }: Props) => {
	const { width: windowWidth } = useWindowDimensions();
	const pathname = usePathname();
	const segments: string[] = pathname.split("/").filter(Boolean);

	const breadcrumbPaths: string[] = segments.map((_, index, array) => "/" + array.slice(0, index + 1).join("/"));
	const lastTwoSegments = segments.slice(-2);

	let breadcrumbsToRender: { name: string; url: string }[];

	if (hierarchy) {
		breadcrumbsToRender = flattenHierarchy(hierarchy);

		// Append the current page segment if not already in the hierarchy
		const lastHierarchyUrl = breadcrumbsToRender[breadcrumbsToRender.length - 1]?.url;
		if (lastHierarchyUrl !== pathname) {
			const lastSegment = segments[segments.length - 1];
			breadcrumbsToRender.push({
				name: formatSegment(lastSegment, idReplacement),
				url: pathname,
			});
		}
	} else {
		breadcrumbsToRender = segments.map((segment, index) => ({
			name: formatSegment(segment, idReplacement),
			url: breadcrumbPaths[index],
		}));
	}

	return (
		<Box mt={12} w="$full">
			<HStack flexWrap="wrap" rowGap={5} columnGap={20}>
				{windowWidth < 1024 && (
					<>
						<Link href="/">
							<Box flexDirection="row" gap={20}>
								<Icon as={Home} color="$primarylightblue" />
								<Text>...</Text>
							</Box>
						</Link>
						<Typography.H5>/</Typography.H5>
						{lastTwoSegments.map((segment: string, index: number) => {
							const isLastSegment = index === lastTwoSegments.length - 1;
							const breadcrumbPath = breadcrumbPaths[index];
							if (isLastSegment) {
								return (
									<Typography key={index} weight="bold">
										{formatSegment(segment, idReplacement)}
									</Typography>
								);
							}
							return (
								<React.Fragment key={index}>
									<Box key={breadcrumbPath}>
										<Link href={breadcrumbPath}>
											<Typography.Paragraph
												sx={{
													borderBottomWidth: 2,
												}}
												weight="normal"
											>
												{formatSegment(segment, idReplacement)}
											</Typography.Paragraph>
										</Link>
									</Box>
									<Typography.H5>/</Typography.H5>
								</React.Fragment>
							);
						})}
					</>
				)}
				{windowWidth >= 1024 && (
					<>
						<Link href="/">
							<Icon as={Home} color="$primarylightblue" />
						</Link>
						<Typography.H5>/</Typography.H5>
						{breadcrumbsToRender.map((crumb, index) => {
							const isLastSegment = index === breadcrumbsToRender.length - 1;
							if (isLastSegment) {
								return (
									<Typography key={index} weight="bold">
										{crumb.name}
									</Typography>
								);
							}
							return (
								<React.Fragment key={index}>
									<Box>
										<Link href={crumb.url}>
											<Typography.Paragraph
												sx={{
													borderBottomWidth: 2,
												}}
												weight="normal"
											>
												{crumb.name}
											</Typography.Paragraph>
										</Link>
									</Box>
									<Typography.H5>/</Typography.H5>
								</React.Fragment>
							);
						})}
					</>
				)}
			</HStack>
		</Box>
	);
};

export default Breadcrumbs;
